import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { categoryExpertsAdmin, type CategoryExpertAssignment } from '@/lib/api/calendlyClient';
import { staffUsersAdmin, type StaffUser } from '@/lib/api/staffUsersClient';
import { cccdAdmin, type AdminEvent, type AdminRegionalEvent, type EventCategory } from '@/lib/api/cccdAdminClient';
import ModuleLayout from '@/components/admin/ModuleLayout';
import { TrashBinIcon } from '@/icons';
import { toastError, toastSuccess } from '@/lib/toast';
import { showUndoToast } from '@/lib/undoToast';
import { LoadState } from "@/components/ui/LoadState"

const inputCls =
  'h-10 w-full rounded-lg border border-gray-300 px-3 text-[13px] text-gray-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white';

function toDateInputValue(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

// cccd-event-scoped-experts-and-availability: la asignación experto↔categoría
// ahora vive SIEMPRE dentro de un evento o regional específico — dos
// eventos con una categoría del mismo slug (p. ej. "avanzada") nunca se
// mezclan. El experto conecta su propio Calendly desde "Mi cuenta" — esta
// página solo asigna la relación evento+categoría↔experto.
export default function CategoryExpertsPage() {
  const [scope, setScope] = useState<'national' | 'regional'>('national');
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [regionals, setRegionals] = useState<AdminRegionalEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedRegionalId, setSelectedRegionalId] = useState('');
  const [categories, setCategories] = useState<EventCategory[]>([]);

  const [assignments, setAssignments] = useState<CategoryExpertAssignment[]>([]);
  const [staff, setStaff] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(false);

  const [newCategorySlug, setNewCategorySlug] = useState('');
  const [newUserId, setNewUserId] = useState('');
  const [newValidFrom, setNewValidFrom] = useState('');
  const [newValidUntil, setNewValidUntil] = useState('');
  const [newMaxMeetings, setNewMaxMeetings] = useState('');
  const [newManualSchedulingUrl, setNewManualSchedulingUrl] = useState('');
  const [creating, setCreating] = useState(false);

  // Eventos/regionales + staff se cargan una sola vez.
  useEffect(() => {
    cccdAdmin.listEvents({ limit: 200 }).then((r) => setEvents(r.events)).catch(() => {});
    cccdAdmin.listRegionals({ limit: 200 }).then((r) => setRegionals(r.regionals)).catch(() => {});
    staffUsersAdmin.list().then(setStaff).catch(() => {});
  }, []);

  const selectedId = scope === 'national' ? selectedEventId : selectedRegionalId;

  const load = useCallback(async () => {
    if (!selectedId) {
      setCategories([]);
      setAssignments([]);
      return;
    }
    setLoading(true);
    try {
      const [cats, assignmentsRes] = await Promise.all([
        scope === 'national' ? cccdAdmin.getEventCategories(selectedId) : cccdAdmin.getRegionalCategories(selectedId),
        categoryExpertsAdmin.list(scope === 'national' ? { eventId: selectedId } : { regionalEventId: selectedId }),
      ]);
      setCategories(cats);
      setAssignments(assignmentsRes);
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'Error al cargar las asignaciones.');
    } finally {
      setLoading(false);
    }
  }, [scope, selectedId]);

  useEffect(() => { void load(); }, [load]);

  async function onCreate() {
    const categorySlug = newCategorySlug.trim();
    if (!categorySlug || !newUserId || !selectedId) return;
    setCreating(true);
    try {
      await categoryExpertsAdmin.create({
        ...(scope === 'national' ? { eventId: selectedId } : { regionalEventId: selectedId }),
        categorySlug,
        userId: newUserId,
        validFrom: newValidFrom ? new Date(newValidFrom).toISOString() : null,
        validUntil: newValidUntil ? new Date(newValidUntil).toISOString() : null,
        maxMeetings: newMaxMeetings ? Number(newMaxMeetings) : null,
        manualSchedulingUrl: newManualSchedulingUrl.trim() || null,
      });
      setNewCategorySlug('');
      setNewUserId('');
      setNewValidFrom('');
      setNewValidUntil('');
      setNewMaxMeetings('');
      setNewManualSchedulingUrl('');
      await load();
      toastSuccess('Experto asignado a la categoría.');
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'No se pudo asignar el experto.');
    } finally {
      setCreating(false);
    }
  }

  function handleDelete(assignment: CategoryExpertAssignment) {
    const snapshot = assignments;
    setAssignments((prev) => prev.filter((a) => a.id !== assignment.id));
    showUndoToast({
      message: `Asignación de "${assignment.user.name}" a "${assignment.categorySlug}" eliminada`,
      onUndo: () => setAssignments(snapshot),
      onCommit: async () => {
        try {
          await categoryExpertsAdmin.remove(assignment.id);
        } catch (err: any) {
          setAssignments(snapshot);
          toastError(err?.response?.data?.error ?? 'No se pudo eliminar la asignación.');
        }
      },
    });
  }

  function statusLabel(a: CategoryExpertAssignment): { label: string; cls: string } {
    const now = Date.now();
    if (a.validFrom && new Date(a.validFrom).getTime() > now) return { label: 'Futura', cls: 'bg-warning-50 text-warning-600 dark:bg-warning-500/10' };
    if (a.validUntil && new Date(a.validUntil).getTime() < now) return { label: 'Expirada', cls: 'bg-gray-100 text-gray-400 dark:bg-gray-800' };
    return { label: 'Activa', cls: 'bg-success-50 text-success-600 dark:bg-success-500/10' };
  }

  return (
    <ModuleLayout
      breadcrumbs={[{ label: 'SateLab', to: '/' }, { label: 'CCCD', to: '/cccd' }, 'Expertos por categoría']}
      title="Expertos por categoría"
      subtitle="Asigna qué experto(s) del staff puede agendar reunión cada equipo, según la categoría de SU evento — la asignación queda ligada a un evento/regional específico, nunca a un slug suelto que se repita entre ediciones. El experto conecta su propio Calendly desde 'Mi cuenta', o puedes pegar directamente su link público de agendamiento para expertos externos que no tienen conexión propia."
      tabs={[]}
      metrics={[]}
      actions={
        selectedId ? (
          <Link
            to={`/cccd/event-meet-windows?scope=${scope}&id=${selectedId}`}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/[0.04]"
          >
            Meets de este evento
          </Link>
        ) : undefined
      }
    >
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:max-w-xl">
        <div>
          <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Alcance</label>
          <select
            value={scope}
            onChange={(e) => { setScope(e.target.value as 'national' | 'regional'); setSelectedEventId(''); setSelectedRegionalId(''); }}
            className={inputCls}
          >
            <option value="national">Evento nacional</option>
            <option value="regional">Evento regional</option>
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Evento</label>
          {scope === 'national' ? (
            <select value={selectedEventId} onChange={(e) => setSelectedEventId(e.target.value)} className={inputCls}>
              <option value="">Seleccionar…</option>
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
          ) : (
            <select value={selectedRegionalId} onChange={(e) => setSelectedRegionalId(e.target.value)} className={inputCls}>
              <option value="">Seleccionar…</option>
              {regionals.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {!selectedId ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Elige un evento o regional para ver y asignar sus expertos por categoría.</p>
      ) : (
        <>
          <div className="mb-5 rounded-2xl border border-dashed border-gray-300 bg-gray-25 p-5 dark:border-gray-700 dark:bg-white/[0.02]">
            <p className="mb-3 text-sm font-semibold text-gray-600 dark:text-gray-400">Asignar experto a una categoría</p>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
              <div className="md:col-span-1">
                <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Categoría</label>
                <select value={newCategorySlug} onChange={(e) => setNewCategorySlug(e.target.value)} className={inputCls}>
                  <option value="">Seleccionar…</option>
                  {categories.map((cat) => <option key={cat.id} value={cat.slug}>{cat.name}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Experto</label>
                <select value={newUserId} onChange={(e) => setNewUserId(e.target.value)} className={inputCls}>
                  <option value="">Seleccionar…</option>
                  {staff.map((u) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-1">
                <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Desde</label>
                <input type="date" value={newValidFrom} onChange={(e) => setNewValidFrom(e.target.value)} className={inputCls} />
              </div>
              <div className="md:col-span-1">
                <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Hasta</label>
                <input type="date" value={newValidUntil} onChange={(e) => setNewValidUntil(e.target.value)} className={inputCls} />
              </div>
              <div className="md:col-span-1">
                <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Cupo por equipo</label>
                <input type="number" min={1} value={newMaxMeetings} onChange={(e) => setNewMaxMeetings(e.target.value)} placeholder="Sin límite" className={inputCls} />
              </div>
              <div className="md:col-span-2">
                <label className="mb-1.5 block text-[11px] text-gray-500 dark:text-gray-400">Link manual de Calendly (experto externo, opcional)</label>
                <input
                  type="url"
                  value={newManualSchedulingUrl}
                  onChange={(e) => setNewManualSchedulingUrl(e.target.value)}
                  placeholder="https://calendly.com/experto/revision-cohetes"
                  className={inputCls}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={onCreate}
              disabled={!newCategorySlug.trim() || !newUserId || creating}
              className="mt-3 h-10 whitespace-nowrap rounded-lg bg-brand-500 px-[18px] text-[13px] font-medium text-white hover:bg-brand-600 disabled:opacity-60"
            >
              {creating ? 'Asignando…' : '+ Asignar experto'}
            </button>
          </div>

          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="grid grid-cols-[1fr_1.5fr_1fr_1fr_1fr_auto_auto] gap-3 border-b border-gray-100 bg-gray-25 px-5 py-3 dark:border-gray-800 dark:bg-white/[0.02]">
              {['Categoría', 'Experto', 'Desde', 'Hasta', 'Cupo/equipo', 'Estado', ''].map((h, i) => (
                <span key={i} className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{h}</span>
              ))}
            </div>

            {loading ? (
              <LoadState />
            ) : assignments.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-gray-500 dark:text-gray-400">Sin asignaciones todavía para este evento.</p>
            ) : (
              assignments.map((a) => {
                const status = statusLabel(a);
                return (
                  <div key={a.id} className="grid grid-cols-[1fr_1.5fr_1fr_1fr_1fr_auto_auto] items-center gap-3 border-b border-gray-100 px-5 py-3 last:border-b-0 dark:border-gray-800">
                    <span className="truncate text-[13px] text-gray-700 dark:text-gray-300">{a.categorySlug}</span>
                    <div className="min-w-0">
                      <p className="truncate text-[13px] text-gray-700 dark:text-gray-300">{a.user.name}</p>
                      <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                        {a.manualSchedulingUrl
                          ? 'Link manual configurado'
                          : a.user.calendlySchedulingUrl
                            ? (a.user.calendlyWebhooksEnabled ? 'Calendly conectado · webhooks activos' : 'Calendly conectado · modo básico (clic)')
                            : 'Calendly aún no conectado'}
                      </p>
                    </div>
                    <span className="text-[13px] text-gray-500 dark:text-gray-400">{toDateInputValue(a.validFrom) || '—'}</span>
                    <span className="text-[13px] text-gray-500 dark:text-gray-400">{toDateInputValue(a.validUntil) || '—'}</span>
                    <span className="text-[13px] text-gray-500 dark:text-gray-400">{a.maxMeetings ?? 'Sin límite'}</span>
                    <span className={`w-fit rounded-full px-2.5 py-1 text-xs ${status.cls}`}>{status.label}</span>
                    <button
                      type="button"
                      onClick={() => handleDelete(a)}
                      title="Eliminar"
                      className="flex size-7 items-center justify-center rounded-lg border border-gray-100 text-gray-400 hover:bg-error-50 hover:text-error-600 dark:border-gray-800 dark:hover:bg-error-500/10"
                    >
                      <TrashBinIcon className="size-3.5" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </ModuleLayout>
  );
}
